import React, { Suspense, useEffect, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import { Snackbar, Switch } from '../../../../components/atoms';
import {
  Add as AddIcon,
  RestaurantVoucher as RestaurantVoucherIcon,
} from '../../../../components/icons';
import { LoadingSpinner } from '../../../../components/layouts';
import { SectionGroup } from '../../../../components/molecules';
import {
  SavedRestaurantVoucher,
  VoucherStatus,
} from '../../../../core/state/payment/payment.types';
import { useInterval } from '../../../../utils/hooks';
import { Track } from '../../../../weborder/analytics';
import * as Api from '../../../../weborder/Api';
import { SectionTextButton } from '../../components';
import { DispatchProps, StateProps } from './index';
import {
  AddVoucherDialog,
  BalanceOutstanding,
  VoucherOptions,
} from './partials';
import { restaurantVoucherUtils } from './utils';

export type OuterProps = {
  currency: Api.Currency;
  orderTotalToPay: number;
  storeId: number;
};
type Props = OuterProps & StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  restaurantVoucherSection: {
    '&-content': {
      paddingBottom: theme.spacing(5.25),

      [theme.breakpoints.up('md')]: {
        paddingBottom: theme.spacing(7.25),
      },
    },
  },

  warningMessage: {
    marginTop: theme.spacing(2),
  },
}));

export function RestaurantVoucherSection(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    actions,
    canApplyRestaurantVoucher,
    canRestaurantVouchersCoverOrderAmount,
    currency,
    currentLang,
    isRestaurantVouchersBalanceUnknown,
    menuItems,
    orderTotalToPay,
    restaurantVouchers,
    storeId,
    selectedPaymentOption,
    translate,
  } = props;

  const [selectedVoucherId, setSelectedVoucherId] = useState<number>(null);
  const [voucherStatus, setVoucherStatus] = useState<VoucherStatus>(null);
  const [balanceOutstanding, setBalanceOutstanding] = useState<number>(0);
  const [isAddVoucherDialogOpen, setIsAddVoucherDialogOpen] =
    useState<boolean>(false);
  const [isVoucherSectionEnabled, setIsVoucherSectionEnabled] =
    useState<boolean>(true);

  // ! PayGreen Minimum Viable Product
  const isMVP = true;
  const isSavedVouchersLoading = restaurantVouchers === undefined;
  const isSavedVouchersAvailable = restaurantVouchers?.savedVouchers.length > 0;
  const isRestaurantVoucherPayment =
    !!selectedVoucherId &&
    selectedPaymentOption?.PaymentAccountType === 'PayGreenRestaurantVoucher';
  const hasWarningMessage =
    !canApplyRestaurantVoucher ||
    !canRestaurantVouchersCoverOrderAmount ||
    isRestaurantVouchersBalanceUnknown;

  const showBalanceOutstanding =
    !isMVP && !!selectedVoucherId && isVoucherSectionEnabled;
  const showWarningMessage =
    isSavedVouchersAvailable && isVoucherSectionEnabled && hasWarningMessage;

  useEffect(() => {
    if (!restaurantVouchers) {
      actions.getUserSavedRestaurantVouchers(storeId);
    }
  }, []);

  useEffect(() => {
    // Retrieve the menu items category everytime basket changes
    const menuItemIds = Object.values(menuItems || {})
      .map((i) => i.itemId)
      .filter(Boolean);

    actions.retrieveCategorizeMenuitems(menuItemIds);
  }, [menuItems]);

  useEffect(() => {
    if (!isVoucherSectionEnabled) {
      setSelectedVoucherId(null);

      if (isRestaurantVoucherPayment) {
        actions.selectPaymentOption(null);
      }
    }
  }, [isVoucherSectionEnabled]);

  useEffect(() => {
    if (!isRestaurantVoucherPayment) {
      setSelectedVoucherId(null);
    }
  }, [isRestaurantVoucherPayment]);

  useEffect(() => {
    const canSelectVoucher =
      isSavedVouchersAvailable &&
      isVoucherSectionEnabled &&
      canApplyRestaurantVoucher &&
      isRestaurantVoucherPayment;

    if (canSelectVoucher) {
      selectAvailableVoucher();
    }
  }, [orderTotalToPay]);

  useEffect(() => {
    const canSelectVoucher =
      isSavedVouchersAvailable &&
      isVoucherSectionEnabled &&
      canApplyRestaurantVoucher;

    if (canSelectVoucher) {
      selectAvailableVoucher();
    }
  }, [
    canApplyRestaurantVoucher,
    isVoucherSectionEnabled,
    restaurantVouchers?.savedVouchers.length,
  ]);

  useEffect(() => {
    if (voucherStatus?.isPolling) {
      const addedVoucher = restaurantVoucherUtils.getSelectedVoucher(
        voucherStatus.voucherId,
        restaurantVouchers?.savedVouchers,
      );

      if (addedVoucher) {
        Track('Payments', 'Add New Restaurant Voucher Complete');

        setVoucherStatus({
          ...voucherStatus,
          isPolling: false,
          status: 'Active',
        });
      }
    }
  }, [restaurantVouchers?.savedVouchers.length]);

  const getVoucherStatus = (): void => {
    if (voucherStatus?.voucherId) {
      actions.getUserSavedRestaurantVoucherStatus(
        voucherStatus.voucherId,
        storeId,
      );
    }
  };

  const finishVoucherStatusPolling = (): void => {
    setVoucherStatus({
      ...voucherStatus,
      isPolling: false,
    });
  };

  const pollEveryThreeSeconds = 3000;
  const pollingDelay = voucherStatus?.isPolling ? pollEveryThreeSeconds : null;
  const pollForFourMinutes = 240000;

  useInterval(
    getVoucherStatus,
    pollingDelay,
    pollForFourMinutes,
    finishVoucherStatusPolling,
  );

  const selectAvailableVoucher = (): void => {
    if (!selectedVoucherId) {
      return selectFirstAvailableVoucher();
    }

    const selectedVoucher = restaurantVoucherUtils.getSelectedVoucher(
      selectedVoucherId,
      restaurantVouchers.savedVouchers,
    );

    const isVoucherAvailable =
      restaurantVoucherUtils.checkVoucherCoversFullAmount(
        selectedVoucher.AvailableBalance,
        orderTotalToPay,
      );

    return isVoucherAvailable
      ? selectVoucher(selectedVoucher)
      : selectFirstAvailableVoucher();
  };

  const selectFirstAvailableVoucher = (): void => {
    const firstAvailableVoucher =
      restaurantVoucherUtils.getFirstAvailableVoucher(
        orderTotalToPay,
        restaurantVouchers.savedVouchers,
      );

    if (firstAvailableVoucher) {
      selectVoucher(firstAvailableVoucher);
    } else {
      setSelectedVoucherId(null);
      actions.selectPaymentOption(null);
    }
  };

  const selectVoucher = (voucher: SavedRestaurantVoucher): void => {
    const { PaymentAccountId: voucherId, AvailableBalance: availableBalance } =
      voucher;

    const balance = restaurantVoucherUtils.getBalanceOutstanding(
      availableBalance,
      orderTotalToPay,
    );

    setSelectedVoucherId(voucherId);
    setBalanceOutstanding(balance);
    actions.selectPaymentOption(String(voucherId));
  };

  const handleOnSelectVoucher = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const selectedVoucher = restaurantVoucherUtils.getSelectedVoucher(
      Number(event.target.value),
      restaurantVouchers.savedVouchers,
    );

    selectVoucher(selectedVoucher);
  };

  const toggleVoucherSectionEnabled = (): void =>
    setIsVoucherSectionEnabled(!isVoucherSectionEnabled);

  const toggleAddVoucherDialog = (): void =>
    setIsAddVoucherDialogOpen(!isAddVoucherDialogOpen);

  const getWarningMessage = (): string => {
    if (!canApplyRestaurantVoucher) {
      return `${translate('RestaurantVoucher_containsAlcohol_errorMessage')}`;
    }

    if (!canRestaurantVouchersCoverOrderAmount) {
      return `${translate('RestaurantVoucher_insufficientFunds_errorMessage')}`;
    }

    return `${translate('RestaurantVoucher_balanceUnknown_errorMessage')}`;
  };

  const renderDisableVoucherSectionSwitch = (): JSX.Element => (
    <Switch
      checked={isSavedVouchersAvailable ? isVoucherSectionEnabled : false}
      dataFd="restaurant-voucher-switch"
      disabled={!isSavedVouchersAvailable}
      onChange={toggleVoucherSectionEnabled}
    />
  );

  const renderVoucherOptions = (): JSX.Element =>
    isSavedVouchersAvailable ? (
      <VoucherOptions
        currency={currency}
        currentLang={currentLang}
        isVoucherSectionDisabled={
          !isVoucherSectionEnabled || !canApplyRestaurantVoucher
        }
        onChange={handleOnSelectVoucher}
        orderTotalToPay={orderTotalToPay}
        selectedVoucherId={selectedVoucherId}
        translate={translate}
        voucherOptions={restaurantVouchers.savedVouchers}
      />
    ) : null;

  return (
    <SectionGroup
      className={classes.restaurantVoucherSection}
      icon={<RestaurantVoucherIcon viewBox="0, 0, 24, 18" />}
      sectionName="restaurant-vouchers"
      title={`${translate('RestaurantVoucher_title')}`}
      titleSecondary={renderDisableVoucherSectionSwitch()}
    >
      {isSavedVouchersLoading ? (
        <LoadingSpinner variant="panel" />
      ) : (
        renderVoucherOptions()
      )}

      <SectionTextButton
        border={isSavedVouchersAvailable ? 'bottom' : 'both'}
        data-fd="restaurant-voucher-add-new-button"
        disabled={isSavedVouchersLoading || !isVoucherSectionEnabled}
        onClick={toggleAddVoucherDialog}
        startIcon={<AddIcon />}
        text={`${translate('RestaurantVoucher_addNew')}`}
      />

      {showBalanceOutstanding && (
        <BalanceOutstanding
          balanceOutstanding={balanceOutstanding}
          currency={currency}
          currentLang={currentLang}
          translate={translate}
        />
      )}

      {showWarningMessage && (
        <Snackbar
          className={classes.warningMessage}
          message={getWarningMessage()}
          severity="warning"
        />
      )}

      <Suspense fallback={<LoadingSpinner />}>
        <AddVoucherDialog
          actions={actions}
          handleCloseDialog={toggleAddVoucherDialog}
          isDialogOpen={isAddVoucherDialogOpen}
          setVoucherStatus={setVoucherStatus}
          storeId={storeId}
          translate={translate}
          voucherOptions={restaurantVouchers?.acceptedVouchers}
        />
      </Suspense>
    </SectionGroup>
  );
}
